import createFetchClient, { type Middleware } from "openapi-fetch";
import createQueryClient from "openapi-react-query";
import { recordApiFailure } from "./failures";
import type { paths } from "./schema";

/** Supplies the current access token; the app wires this to the OIDC session (tokens live in memory). */
export type AccessTokenProvider = () => string | undefined;

/**
 * Headers to add to every request, read at request time rather than at construction: a support session
 * starts and ends inside one page's life (US-14.3), and rebuilding the client for it would throw away
 * the query cache along with it.
 */
export type ExtraHeadersProvider = () => Record<string, string>;

/**
 * The language the application is being read in, for {@code Accept-Language} (US-23.4).
 *
 * <p>Read at request time, not at construction: switching the language must change the next answer,
 * not require a reload. Without it the header would be the browser's own preference, which is a
 * different question from "which language is this person reading the screen in" — and the server
 * answers a product's description, a problem detail and a validation message from it.
 */
export type LanguageProvider = () => string | undefined;

export interface ApiClientOptions {
  baseUrl: string;
  getAccessToken?: AccessTokenProvider;
  getExtraHeaders?: ExtraHeadersProvider;
  getLanguage?: LanguageProvider;
}

/**
 * Typed REST client generated from the backend's OpenAPI document. Every request carries the bearer
 * token when one is available; errors come back as RFC 9457 problem documents (see {@link ProblemDetail}).
 */
export function createApiClient({
  baseUrl,
  getAccessToken,
  getExtraHeaders,
  getLanguage,
}: ApiClientOptions) {
  const fetchClient = createFetchClient<paths>({ baseUrl });

  const authMiddleware: Middleware = {
    async onRequest({ request }) {
      const token = getAccessToken?.();
      if (token) {
        request.headers.set("Authorization", `Bearer ${token}`);
      }
      const language = getLanguage?.();
      if (language) {
        request.headers.set("Accept-Language", language);
      }
      Object.entries(getExtraHeaders?.() ?? {}).forEach(([name, value]) => {
        request.headers.set(name, value);
      });
      return request;
    },
  };
  fetchClient.use(authMiddleware);

  // Every refusal is remembered for a moment, so a bug report can say which call failed (EP-53).
  const failureMiddleware: Middleware = {
    async onResponse({ request, response }) {
      if (response.ok) {
        return undefined;
      }
      let code: string | undefined;
      if ((response.headers.get("content-type") ?? "").includes("json")) {
        try {
          const body: unknown = await response.clone().json();
          if (typeof body === "object" && body !== null && typeof (body as { code?: unknown }).code === "string") {
            code = (body as { code: string }).code;
          }
        } catch {
          // A body that is not JSON after all: the status and the request id are still worth keeping.
        }
      }
      recordApiFailure({
        method: request.method,
        path: new URL(request.url).pathname,
        status: response.status,
        code,
        requestId: response.headers.get("X-Request-Id") ?? undefined,
        at: new Date().toISOString(),
      });
      return undefined;
    },
  };
  fetchClient.use(failureMiddleware);

  return {
    fetch: fetchClient,
    /** TanStack Query hooks bound to the same client: `api.query.useQuery("get", "/api/v1/me")`. */
    query: createQueryClient(fetchClient),
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;
